import React, { Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, Outlet } from 'react-router-dom';
import { ConfigProvider, Spin, theme as antdTheme } from 'antd';
import zhCN from 'antd/locale/zh_CN';
import enUS from 'antd/locale/en_US';
import { AuthProvider, useAuth } from './context/AuthContext';

import { LocaleProvider, useLocale } from './context/LocaleContext';
import { useAppVersionCheck } from './hooks/useAppVersionCheck';
import { ThemeProvider, useTheme } from './context/ThemeContext';
import StoreLayout from './components/StoreLayout';
import Login from './pages/Login';
import Register from './pages/Register';
import RegisterSuccess from './pages/RegisterSuccess';

// Lazy load pages — build: 20260624-03
const StoreDashboard = React.lazy(() => import('./pages/store/StoreDashboard'));
const StoreOrderList = React.lazy(() => import('./pages/store/StoreOrderList'));
const StoreOrderDetail = React.lazy(() => import('./pages/store/StoreOrderDetail'));
const AfterSalesChat = React.lazy(() => import('./pages/store/AfterSalesChat'));
const StoreCustomerList = React.lazy(() => import('./pages/store/StoreCustomerList'));
const StoreCustomerDetail = React.lazy(() => import('./pages/store/StoreCustomerDetail'));
const StoreMaterial = React.lazy(() => import('./pages/store/StoreMaterial'));
const StoreAccount = React.lazy(() => import('./pages/store/StoreAccount'));
const StoreBills = React.lazy(() => import('./pages/store/StoreBills'));
const StoreCustomerCreate = React.lazy(() => import('./pages/store/StoreCustomerCreate'));
const StoreShop = React.lazy(() => import('./pages/store/StoreShop'));
const StoreNotifications = React.lazy(() => import('./pages/store/StoreNotifications'));
const OrderCreate = React.lazy(() => import('./pages/OrderCreate'));
const FinanceOrderList = React.lazy(() => import('./pages/finance/FinanceOrderList'));
const FinanceOrderDetail = React.lazy(() => import('./pages/finance/FinanceOrderDetail'));

const PageLoader: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Suspense fallback={<div style={{ padding: 40, textAlign: 'center' }}><Spin size="large" /></div>}>
    {children}
  </Suspense>
);

const ProtectedLayout: React.FC = () => {
  const { user, loading, isStoreCustomer } = useAuth();
  const location = useLocation();

  if (loading) {
    return <div style={{ padding: 40, textAlign: 'center' }}><Spin size="large" /></div>;
  }

  if (!user) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  // user_web_code 仅允许门店客户角色访问
  if (!isStoreCustomer) {
    return <Navigate to="/login" replace />;
  }

  return (
    <StoreLayout>
      <Outlet />
    </StoreLayout>
  );
};

function AppRoutes() {
  // eslint-disable-next-line no-console
  console.log('build-v', '20260624-03');
  const { isStoreCustomer, hasRole } = useAuth();
  // 版本更新提示：登录态就绪后只检查一次（不做定时轮询，避免同一天反复打扰）
  useAppVersionCheck();

  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/register/success" element={<RegisterSuccess />} />
      <Route element={<ProtectedLayout />}>
        <Route path="/" element={<PageLoader><StoreDashboard /></PageLoader>} />
        <Route path="/orders" element={<PageLoader><StoreOrderList /></PageLoader>} />
        <Route path="/orders/create" element={<PageLoader><OrderCreate /></PageLoader>} />
        <Route path="/orders/:id" element={<PageLoader><StoreOrderDetail /></PageLoader>} />
        <Route path="/orders/after-sales/:after_sales_id" element={<PageLoader><AfterSalesChat /></PageLoader>} />
        <Route path="/customers" element={<PageLoader><StoreCustomerList /></PageLoader>} />
        <Route path="/customers/create" element={<PageLoader><StoreCustomerCreate /></PageLoader>} />
        <Route path="/customers/:id" element={<PageLoader><StoreCustomerDetail /></PageLoader>} />
        <Route path="/materials" element={<PageLoader><StoreMaterial /></PageLoader>} />
        <Route path="/account" element={<PageLoader><StoreAccount /></PageLoader>} />
        <Route path="/shop" element={<PageLoader><StoreShop /></PageLoader>} />
        <Route path="/bills" element={<PageLoader><StoreBills /></PageLoader>} />
        <Route path="/notifications" element={<PageLoader><StoreNotifications /></PageLoader>} />
        {/* 以下路由仅非B端门店用户可访问 */}
        {!isStoreCustomer && (
          <>
            <Route path="/finance/orders" element={<PageLoader><FinanceOrderList /></PageLoader>} />
            <Route path="/finance/orders/:id" element={<PageLoader><FinanceOrderDetail /></PageLoader>} />
          </>
        )}
        {/* B端用户访问无权限路由时重定向到首页 */}
        {isStoreCustomer && (
          <Route path="*" element={<Navigate to="/" replace />} />
        )}
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function AppInner() {
  const { locale } = useLocale();
  const { isDark } = useTheme();
  return (
    <ConfigProvider
      locale={locale === 'zh' ? zhCN : enUS}
      theme={{
        token: {
          colorPrimary: '#1a5fb4',
          borderRadius: 6,
          fontSize: 14,
          colorText: '#262626',
          colorTextSecondary: '#595959',
          colorTextTertiary: '#8c8c8c',
          fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, 'Noto Sans', sans-serif",
          controlHeight: 36,
          controlHeightLG: 40,
          controlHeightSM: 28,
        },
        algorithm: isDark ? antdTheme.darkAlgorithm : undefined,
      }}
    >
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
        </AuthProvider>
      </BrowserRouter>
    </ConfigProvider>
  );
}

function App() {
  return (
    <LocaleProvider>
      <ThemeProvider>
        <AppInner />
      </ThemeProvider>
    </LocaleProvider>
  );
}

export default App;
